"use client";

import { Button, Loader, Modal } from "@devhop/ui";
import { PackageIcon } from "lucide-react";
import type {
  PosLotItem,
  ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import { usePosLotsForSkuQuery } from "@/modules/merchant/presentation/api/queries";
import { resolveImageSrc } from "@/shared/ui/AppImage";

type ShopPosLotPickModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  branchId: string;
  row: ShopSellableCatalogRow | null;
  onPick: (row: ShopSellableCatalogRow, lot: PosLotItem) => void;
};

function formatPosLotDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  try {
    return new Date(`${isoDate}T12:00:00`).toLocaleDateString("lo-LA");
  } catch {
    return isoDate;
  }
}

export function ShopPosLotPickModal({
  open,
  onOpenChange,
  shopId,
  branchId,
  row,
  onPick,
}: ShopPosLotPickModalProps) {
  const q = usePosLotsForSkuQuery(shopId, branchId, row?.id, open && !!row);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="ເລືອກ lot"
      description={
        row ? `${row.productName} · SKU ${row.shopSku}` : "ເລືອກສິນຄ້າກ່ອນ"
      }
      size="md"
    >
      {!row ? (
        <p className="text-muted-foreground text-sm">ບໍ່ມີລາຍການ</p>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
              {(() => {
                const src = row.imageUrl ? resolveImageSrc(row.imageUrl) : null;
                return src ? (
                  <img
                    src={src}
                    alt=""
                    className="size-full rounded-lg object-cover"
                  />
                ) : (
                  <PackageIcon className="size-6 text-secondary" />
                );
              })()}
            </div>
            <div className="min-w-0">
              <p className="font-bold font-heading text-card-foreground text-sm">
                {row.productName}
              </p>
              <p className="font-mono text-muted-foreground text-xs">
                {row.shopSku}
              </p>
            </div>
          </div>

          {q.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          ) : q.isError ? (
            <p className="text-destructive text-sm">
              {q.error instanceof Error ? q.error.message : "ໂຫຼດລົ້ມເຫຼວ"}
            </p>
          ) : !q.data?.items.length ? (
            <p className="text-muted-foreground text-sm">
              ບໍ່ມີ lot ທີ່ມີຍອດໃນສາຂານີ້ — ຮັບເຂົ້າກ່ອນ
            </p>
          ) : (
            <ul className="max-h-[min(50vh,20rem)] space-y-2 overflow-y-auto pr-1">
              {q.data.items.map((lot) => {
                const mfg = formatPosLotDate(lot.manufacturedDate);
                const exp = formatPosLotDate(lot.expiryDate);
                return (
                  <li key={lot.id}>
                    <Button
                      type="button"
                      variant="outline"
                      className="h-auto w-full flex-col items-stretch gap-1 border-border py-3 text-start"
                      onClick={() => {
                        onPick(row, lot);
                        onOpenChange(false);
                      }}
                    >
                      <span className="font-mono font-semibold text-sm">
                        Lot {lot.lotNo}
                      </span>
                      <span className="text-xs">
                        {lot.lotScanCode ? (
                          <>
                            ສະແກນ:{" "}
                            <span className="font-medium font-mono">
                              {lot.lotScanCode}
                            </span>
                            {" · "}
                          </>
                        ) : null}
                        ຍອດ:{" "}
                        <span className="font-medium tabular-nums">
                          {lot.qty}
                        </span>
                        {mfg ? (
                          <>
                            {" "}
                            · ຜະລິດ: <span className="font-medium">{mfg}</span>
                          </>
                        ) : null}
                        {exp ? (
                          <>
                            {" "}
                            · ໝົດອາຍຸ: <span className="font-medium">{exp}</span>
                          </>
                        ) : null}
                      </span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
